import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  type ManualEntry,
  type TransactionKind,
  type TransactionRow,
} from '@greencandle/api-client'
import { api } from '../api'

/** Every type in the domain model, which is what FR-B5 asks to be enterable by hand. */
const TYPES: TransactionKind[] = [
  'BUY', 'SELL', 'DIVIDEND', 'INTEREST', 'FEE', 'TAX_WITHHOLDING', 'FX', 'TRANSFER',
  'SPLIT', 'OPTION_EXERCISE', 'OPTION_ASSIGNMENT', 'OPTION_EXPIRY', 'STAKING_REWARD',
  'RENTAL_INCOME',
]

/** The types that open and close lots need an instrument, a quantity and a price. */
const LOT_TYPES = new Set<TransactionKind>([
  'BUY', 'SELL', 'OPTION_EXERCISE', 'OPTION_ASSIGNMENT', 'OPTION_EXPIRY',
])

/**
 * FR-B5, and the editing half of FR-H4.
 *
 * <p>The form changes shape with the type because the underlying rows do: a dividend has an
 * amount and no quantity, a purchase has a quantity and a price and no amount. Showing every
 * field for every type would invite entering a quantity on a fee, which the server would then
 * have to reject - and a form that offers a field it will not accept is worse than one that
 * does not offer it.
 */
export function TransactionForm({
  accountId,
  existing,
  defaultType,
  onDone,
}: {
  accountId: string
  existing?: TransactionRow
  /**
   * What the type starts as for a new entry. The caller usually knows which of the two things
   * this form does is wanted - a trade or a cash movement - and starting on the right one saves
   * the reader deciding a question they have already answered. Ignored when editing, where the
   * type is the row's own and cannot be changed anyway.
   */
  defaultType?: TransactionKind
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const editing = existing !== undefined

  const [type, setType] = useState<TransactionKind>(existing?.type ?? defaultType ?? 'BUY')
  const [tradeDate, setTradeDate] = useState(
    existing?.tradeDate ?? new Date().toISOString().slice(0, 10),
  )
  const [instrumentId, setInstrumentId] = useState(existing?.instrumentId ?? '')
  const [quantity, setQuantity] = useState(existing?.quantity ?? '')
  const [unitPrice, setUnitPrice] = useState(existing?.unitPrice ?? '')
  const [fees, setFees] = useState(existing?.fees ?? '')
  const [amount, setAmount] = useState(existing?.amount ?? '')
  const [note, setNote] = useState(existing?.note ?? '')

  const instruments = useQuery({
    queryKey: ['instruments'],
    queryFn: () => api.instruments(undefined, 0, 300),
  })

  const save = useMutation({
    mutationFn: (entry: ManualEntry) =>
      editing
        ? api.updateTransaction(accountId, existing!.id, entry)
        : api.createTransaction(accountId, entry),
    onSuccess: () => {
      // A trade rewrites lots, which every holding, snapshot and total is derived from.
      void queryClient.invalidateQueries()
      onDone()
    },
  })

  const needsInstrument = LOT_TYPES.has(type)

  function submit(event: FormEvent) {
    event.preventDefault()
    save.mutate({
      type,
      tradeDate,
      instrumentId: needsInstrument ? instrumentId : (instrumentId || null),
      quantity: needsInstrument ? quantity : null,
      unitPrice: needsInstrument ? unitPrice : null,
      fees: needsInstrument ? fees || '0' : null,
      amount: needsInstrument ? null : amount,
      note: note || null,
    })
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 className="card-title">{editing ? 'Edit transaction' : 'New transaction'}</h3>

      <div className="entry-grid">
        <label>
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TransactionKind)}
            disabled={editing}
            title={editing ? 'Delete and re-enter to change the type' : undefined}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>

        <label>
          Date
          <input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)}
                 required />
        </label>

        <label>
          Instrument{needsInstrument ? '' : ' (optional)'}
          <select value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)}>
            <option value="">—</option>
            {(instruments.data?.items ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.symbol} · {i.assetClass}
              </option>
            ))}
          </select>
        </label>

        {needsInstrument ? (
          <>
            <label>
              Quantity
              {/* Text, not number: a crypto quantity has eighteen decimals and a number input
                  will happily round it away before it ever reaches the server. */}
              <input type="text" inputMode="decimal" value={quantity}
                     onChange={(e) => setQuantity(e.target.value)}
                     placeholder={type === 'SELL' ? 'negative, e.g. -10' : 'e.g. 10'} required />
            </label>
            <label>
              Price
              <input type="text" inputMode="decimal" value={unitPrice}
                     onChange={(e) => setUnitPrice(e.target.value)} required />
            </label>
            <label>
              Fees
              <input type="text" inputMode="decimal" value={fees}
                     onChange={(e) => setFees(e.target.value)} placeholder="0" />
            </label>
          </>
        ) : (
          <label>
            Amount
            <input type="text" inputMode="decimal" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   placeholder="negative for money out" required />
          </label>
        )}

        <label style={{ gridColumn: '1 / -1' }}>
          Note
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>

      {save.isError && (
        <div className="notice error" role="alert" style={{ marginTop: 12 }}>
          {save.error instanceof ApiError
            ? (save.error.problem.detail ?? save.error.message)
            : 'Could not save.'}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" className="primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add transaction'}
        </button>
        <button type="button" onClick={onDone}>Cancel</button>
        {needsInstrument && (
          <span className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>
            Saving a trade rebuilds this account's lots from its whole history.
          </span>
        )}
      </div>
    </form>
  )
}
